/**
 * Utility functions for chat history management
 */

import { ChatMessage, ChatThread } from '../types/chat';
import { stripMarkdown } from './contentCleaning';

const CHAT_THREADS_KEY = 'chatThreads';
const ACTIVE_THREAD_KEY = 'activeThreadId';
const OLD_CHAT_HISTORY_KEY = 'chatHistory';

/**
 * Generate a UUID for new threads
 */
export const generateThreadId = (): string => {
  return crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generate a title from the first user message in a thread
 * Truncates at 40 characters with ellipsis
 */
export const generateThreadTitle = (messages: ChatMessage[]): string => {
  const firstUserMessage = messages.find((m) => m.role === 'user');

  if (!firstUserMessage) {
    return 'New Chat';
  }

  // Extract text content
  let text: string;
  if (typeof firstUserMessage.content === 'string') {
    text = firstUserMessage.content;
  } else {
    // ContentBlock array - find text block
    const textBlock = firstUserMessage.content.find((block) => block.type === 'text');
    text = textBlock?.text || '';
  }

  // Handle image-only messages
  if (!text && firstUserMessage.images?.length) {
    return '[Image]';
  }

  if (!text) {
    return 'New Chat';
  }

  // Clean up and truncate
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= 40) {
    return cleaned;
  }

  return cleaned.substring(0, 40) + '...';
};

/**
 * Format a date as relative time
 * Returns: "Just now", "5 minutes ago", "2 hours ago", "Yesterday", "Dec 15"
 */
export const formatRelativeTime = (isoDate: string): string => {
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    }

    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    }

    if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }

    if (diffDays === 1) {
      return 'Yesterday';
    }

    if (diffDays < 7) {
      return `${diffDays} days ago`;
    }

    // For older dates, show month and day
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return 'Unknown';
  }
};

/**
 * Group threads by date category for display
 */
export const groupThreadsByDate = (
  threads: ChatThread[],
): {
  today: ChatThread[];
  yesterday: ChatThread[];
  lastWeek: ChatThread[];
  older: ChatThread[];
} => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
  const weekStart = new Date(todayStart.getTime() - 7 * 24 * 60 * 60 * 1000);

  const groups = {
    today: [] as ChatThread[],
    yesterday: [] as ChatThread[],
    lastWeek: [] as ChatThread[],
    older: [] as ChatThread[],
  };

  threads.forEach((thread) => {
    const threadDate = new Date(thread.updatedAt);

    if (threadDate >= todayStart) {
      groups.today.push(thread);
    } else if (threadDate >= yesterdayStart) {
      groups.yesterday.push(thread);
    } else if (threadDate >= weekStart) {
      groups.lastWeek.push(thread);
    } else {
      groups.older.push(thread);
    }
  });

  return groups;
};

/**
 * Migrate old single-thread chat history to new multi-thread format
 * Returns null if no migration needed
 */
export const migrateOldChatHistory = (): { threads: ChatThread[]; activeThreadId: string | null } | null => {
  try {
    // Check if new format already exists
    const existingThreads = localStorage.getItem(CHAT_THREADS_KEY);
    if (existingThreads) {
      return null; // Already migrated
    }

    // Check for old format
    const oldHistory = localStorage.getItem(OLD_CHAT_HISTORY_KEY);
    if (!oldHistory) {
      return null; // Nothing to migrate
    }

    const messages: ChatMessage[] = JSON.parse(oldHistory);
    if (!messages || messages.length === 0) {
      // Clean up empty old history
      localStorage.removeItem(OLD_CHAT_HISTORY_KEY);
      return null;
    }

    // Create a single thread from old messages
    const threadId = generateThreadId();
    const now = new Date().toISOString();

    // Try to get timestamp from first message, fall back to now
    const firstTimestamp = messages[0]?.timestamp || now;
    const lastTimestamp = messages[messages.length - 1]?.timestamp || now;

    const thread: ChatThread = {
      id: threadId,
      title: generateThreadTitle(messages),
      messages,
      createdAt: firstTimestamp,
      updatedAt: lastTimestamp,
    };

    // Save new format
    localStorage.setItem(CHAT_THREADS_KEY, JSON.stringify([thread]));
    localStorage.setItem(ACTIVE_THREAD_KEY, threadId);

    // Remove old format
    localStorage.removeItem(OLD_CHAT_HISTORY_KEY);

    return { threads: [thread], activeThreadId: threadId };
  } catch (error) {
    console.error('Failed to migrate chat history:', error);
    return null;
  }
};

/**
 * Load threads from localStorage
 */
export const loadThreads = (): ChatThread[] => {
  try {
    const stored = localStorage.getItem(CHAT_THREADS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
};

/**
 * Load active thread ID from localStorage
 */
export const loadActiveThreadId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_THREAD_KEY);
  } catch {
    return null;
  }
};

/**
 * Strip images from messages to reduce localStorage size
 * Images are large base64 strings that quickly fill quota
 */
const stripImagesFromMessages = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.map((msg) => {
    if (msg.images) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { images, ...rest } = msg;
      return rest;
    }
    return msg;
  });
};

/**
 * Result from saveThreads operation
 */
export type SaveThreadsResult = {
  success: boolean;
  trimmed?: boolean; // true if older threads were removed to fit
};

/**
 * Save threads to localStorage with quota handling
 * Note: Images are stripped from messages before saving to conserve space
 * Returns result indicating if threads were trimmed
 */
export const saveThreads = (threads: ChatThread[]): SaveThreadsResult => {
  // Strip images from all messages before saving to reduce storage size
  const threadsForStorage = threads.map((thread) => ({
    ...thread,
    messages: stripImagesFromMessages(thread.messages),
  }));

  try {
    localStorage.setItem(CHAT_THREADS_KEY, JSON.stringify(threadsForStorage));
    return { success: true };
  } catch (error) {
    // Handle quota exceeded
    if (error instanceof DOMException && error.code === 22) {
      console.warn('LocalStorage quota exceeded, trimming chat threads');

      // Try progressively smaller thread counts
      const trimAttempts = [50, 25, 10, 5];

      for (const keepCount of trimAttempts) {
        if (threadsForStorage.length <= keepCount) continue;

        // Keep most recent threads (already have images stripped)
        const trimmed = threadsForStorage.slice(-keepCount);
        try {
          localStorage.setItem(CHAT_THREADS_KEY, JSON.stringify(trimmed));
          console.log(`Successfully saved ${keepCount} chat threads`);
          return { success: true, trimmed: true };
        } catch {
          continue;
        }
      }

      // Even with minimal threads, quota still exceeded
      // Keep what we have in memory, just can't persist
      console.error('Unable to save threads after trimming');
      return { success: false, trimmed: true };
    } else {
      throw error;
    }
  }
};

/**
 * Save active thread ID to localStorage
 */
export const saveActiveThreadId = (id: string | null): void => {
  try {
    if (id) {
      localStorage.setItem(ACTIVE_THREAD_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_THREAD_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Create a new empty thread
 */
export const createNewThread = (): ChatThread => {
  const now = new Date().toISOString();
  return {
    id: generateThreadId(),
    title: 'New Chat',
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
};

/**
 * Get preview text for a thread (first 60 chars of last message)
 */
export const getThreadPreview = (thread: ChatThread): string => {
  if (thread.messages.length === 0) {
    return 'No messages yet';
  }

  const lastMessage = thread.messages[thread.messages.length - 1];
  let text: string;

  if (typeof lastMessage.content === 'string') {
    text = lastMessage.content;
  } else {
    const textBlock = lastMessage.content.find((block) => block.type === 'text');
    text = textBlock?.text || '';
  }

  if (!text && lastMessage.images?.length) {
    return '[Image attachment]';
  }

  // Strip markdown formatting to show clean preview text
  const cleaned = stripMarkdown(text);
  if (cleaned.length <= 60) {
    return cleaned;
  }

  return cleaned.substring(0, 60) + '...';
};
